/**
 * React Query hook for site weather.
 *
 * Normal fetches use the backend-cached endpoint.
 * Manual refresh uses ?refresh=true (backend cooldown is authoritative).
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSiteWeather, classifyWeatherError } from '../api/weatherApi';

export const weatherQueryKey = (siteId) => ['siteWeather', siteId];

const unwrapWeather = (response) => {
  if (response?.success === false) {
    const err = new Error(response?.error || 'Weather request failed');
    err.errorCode = response?.error_code || null;
    throw err;
  }
  return response?.data || response;
};

export function useSiteWeather(siteId, options = {}) {
  const queryClient = useQueryClient();
  const id = String(siteId || '').trim();
  const enabled = Boolean(id) && (options.enabled ?? true);

  const query = useQuery({
    queryKey: weatherQueryKey(id),
    queryFn: async () => unwrapWeather(await getSiteWeather(id, { refresh: false })),
    enabled,
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  const refresh = useCallback(async () => {
    if (!id) return null;
    const result = await queryClient.fetchQuery({
      queryKey: weatherQueryKey(id),
      queryFn: async () => unwrapWeather(await getSiteWeather(id, { refresh: true })),
    });
    return result;
  }, [id, queryClient]);

  const errorClass = query.error ? classifyWeatherError(query.error) : null;
  const classified =
    query.error?.errorCode === 'WEATHER_LOCATION_MISSING'
      ? 'WEATHER_LOCATION_MISSING'
      : errorClass;

  return {
    weather: query.data || null,
    isLoading: query.isLoading && !query.data,
    isFetching: query.isFetching,
    isRefreshing: query.isFetching && Boolean(query.data),
    isError: query.isError && !query.data,
    error: query.error,
    errorKind: classified,
    stale: Boolean(query.data?.stale),
    refreshCooledDown: Boolean(query.data?.refreshCooledDown),
    providerFetched: Boolean(query.data?.providerFetched),
    refetch: query.refetch,
    refresh,
  };
}
